import { AccountProfile, OptionsApi } from '@core/swagger-client';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

const ChunkSize = 25;

interface StartOptions {
    /** Called once every chunk has been uploaded successfully. */
    onComplete?: () => void;
    /** Called if a chunk fails to upload (the upload stops). */
    onError?: () => void;
}

/**
 * Uploads recipients to an existing e-mail list in fixed-size chunks, driving a small state machine
 * so the progress dialog can report how far along it is. Shared by the create-import flow (which
 * wraps it with create + finalize) and the append-to-an-existing-list flow.
 */
export const useRecipientChunkUpload = () => {
    const addRecipientsMutation = useMutation(OptionsApi.emailLists.addRecipients());

    const [listId, setListId] = useState<string>();
    const [chunks, setChunks] = useState<AccountProfile[][]>([]);
    const [chunkIndex, setChunkIndex] = useState(-1);
    const [active, setActive] = useState(false);
    const callbacks = useRef<StartOptions>({});

    const start = (targetListId: string, recipients: AccountProfile[], options: StartOptions = {}) => {
        const splitChunks: AccountProfile[][] = [];
        const remaining = [...recipients];
        while (remaining.length > 0) {
            splitChunks.push(remaining.splice(0, ChunkSize));
        }

        callbacks.current = options;
        setListId(targetListId);
        setChunks(splitChunks);
        setChunkIndex(0);
        setActive(true);
    };

    useEffect(() => {
        if (!active || !listId || chunkIndex < 0) {
            return;
        }

        if (chunkIndex >= chunks.length) {
            setActive(false);
            callbacks.current.onComplete?.();
            return;
        }

        addRecipientsMutation.mutate(
            { id: listId, recipients: chunks[chunkIndex] },
            {
                onError: () => {
                    setActive(false);
                    callbacks.current.onError?.();
                },
                onSuccess: () => setChunkIndex((index) => index + 1)
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, listId, chunkIndex, chunks]);

    const progressTotal = chunks.length > 0 ? (chunks.length - 1) * ChunkSize + chunks[chunks.length - 1].length : 0;

    return {
        start,
        isUploading: active,
        progressCurrent: Math.min(chunkIndex * ChunkSize, progressTotal),
        progressTotal,
        error: addRecipientsMutation.error
    };
};
